import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../select";
import React, { useState, useEffect } from "react";
import { Sheet, SheetTrigger, SheetContent } from "../sheet";
import { Calendar } from "../calendar";
import { Button } from "../button";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import * as Icons from "./Icons";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useOpportunityFilters } from "@/hooks/useOpportunityFilters";
import { Badge } from "../badge";
import { Input } from "../input";
import type { OpportunityStatus } from "@/hooks/useOpportunities";

export type OpportunityStatusFilter = OpportunityStatus | 'all';

export interface OpportunityFilters {
  tags: string[];
  minPrice: number | null;
  maxPrice: number | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  status: OpportunityStatusFilter;
}

interface MoreFilterCRMProps {
  currentFilters?: OpportunityFilters;
  onFilterChange?: (filters: OpportunityFilters) => void;
  onClearFilters?: () => void;
}

const MoreFilterCRM: React.FC<MoreFilterCRMProps> = ({
  currentFilters = {
    tags: [],
    minPrice: null,
    maxPrice: null,
    dateFrom: null,
    dateTo: null,
    status: 'open'
  },
  onFilterChange,
  onClearFilters
}) => {
  const { uniqueTags, loading: loadingFilters } = useOpportunityFilters();

  const [localTag, setLocalTag] = useState<string>(currentFilters?.tags[0] || '');
  const [localMinPrice, setLocalMinPrice] = useState<string>(currentFilters?.minPrice?.toString() || '');
  const [localMaxPrice, setLocalMaxPrice] = useState<string>(currentFilters?.maxPrice?.toString() || '');
  const [localDateFrom, setLocalDateFrom] = useState<Date | undefined>(currentFilters?.dateFrom || undefined);
  const [localDateTo, setLocalDateTo] = useState<Date | undefined>(currentFilters?.dateTo || undefined);
  const [localStatus, setLocalStatus] = useState<OpportunityStatusFilter>(currentFilters?.status || 'open');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (currentFilters) {
      setLocalTag(currentFilters.tags[0] || '');
      setLocalMinPrice(currentFilters.minPrice?.toString() || '');
      setLocalMaxPrice(currentFilters.maxPrice?.toString() || '');
      setLocalDateFrom(currentFilters.dateFrom || undefined);
      setLocalDateTo(currentFilters.dateTo || undefined);
      setLocalStatus(currentFilters.status || 'open');
    }
  }, [currentFilters]);

  const handleApplyFilters = () => {
    if (!onFilterChange) {
      console.warn('[MoreFilterCRM] onFilterChange nao esta definido');
      setIsOpen(false);
      return;
    }

    const newFilters: OpportunityFilters = {
      tags: localTag ? [localTag] : [],
      minPrice: localMinPrice ? parseFloat(localMinPrice) : null,
      maxPrice: localMaxPrice ? parseFloat(localMaxPrice) : null,
      dateFrom: localDateFrom || null,
      dateTo: localDateTo || null,
      status: localStatus,
    };

    onFilterChange(newFilters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    setLocalTag('');
    setLocalMinPrice('');
    setLocalMaxPrice('');
    setLocalDateFrom(undefined);
    setLocalDateTo(undefined);
    setLocalStatus('open');

    if (onClearFilters) {
      onClearFilters();
    }
    setIsOpen(false);
  };

  const activeFiltersCount = currentFilters ? [
    currentFilters.tags.length > 0,
    currentFilters.minPrice !== null,
    currentFilters.maxPrice !== null,
    currentFilters.dateFrom,
    currentFilters.dateTo,
    currentFilters.status !== 'open'
  ].filter(Boolean).length : 0;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger>
        <div className="border min-h-[40px] px-3 border-neutral-500 bg-neutral-600 hover:bg-neutral-500 transition-colors rounded-[6px] flex justify-center items-center gap-2 relative">
          <p className="text-neutral-50 font-medium text-[14px]">
            Mais filtros
          </p>
          <Icons.filter className="w-[16px] h-[16px]" />
          {activeFiltersCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-blue-600 hover:bg-blue-600 text-white text-xs">
              {activeFiltersCount}
            </Badge>
          )}
        </div>
      </SheetTrigger>
      <SheetContent className="bg-[#18181B] p-3 flex flex-col gap-4">
        <div className="w-full gap-2 flex justify-center items-center">
          <p className="text-[16px] font-medium text-neutral-100">
            Mais filtros
          </p>
          <Icons.filter
            className="w-[16px] h-[16px]"
            fill="#E4E4E7"
            stroke="stroke-[#E4E4E7]"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Status</label>
          <Select value={localStatus} onValueChange={(v) => setLocalStatus(v as OpportunityStatusFilter)}>
            <SelectTrigger className="w-full !text-neutral-50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="open">Abertos</SelectItem>
                <SelectItem value="won">Ganhos</SelectItem>
                <SelectItem value="lost">Perdidos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Tags</label>
          <Select value={localTag} onValueChange={setLocalTag}>
            <SelectTrigger className="w-full !text-neutral-50">
              <SelectValue placeholder={loadingFilters ? "Carregando..." : uniqueTags.length === 0 ? "Nenhuma tag encontrada" : "Selecione uma tag"} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">Todas</SelectItem>
                {uniqueTags.map((tag) => (
                  <SelectItem key={tag.name} value={tag.name}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Valor Mínimo (R$)</label>
          <Input
            type="number"
            value={localMinPrice}
            onChange={(e) => setLocalMinPrice(e.target.value)}
            placeholder="0,00"
            className="w-full !text-neutral-50 bg-[#18181B] border-[#3F3F464D]"
            min="0"
            step="0.01"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Valor Máximo (R$)</label>
          <Input
            type="number"
            value={localMaxPrice}
            onChange={(e) => setLocalMaxPrice(e.target.value)}
            placeholder="0,00"
            className="w-full !text-neutral-50 bg-[#18181B] border-[#3F3F464D]"
            min="0"
            step="0.01"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Data Inicial</label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-neutral-50 border border-[#3F3F464D] !bg-[#18181B]"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateFrom && "text-muted-foreground"
                )}
              >
                {localDateFrom ? format(localDateFrom, "PPP", { locale: ptBR }) : <span>Selecione uma data</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateFrom}
                onSelect={setLocalDateFrom}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Data Final</label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-neutral-50 border border-[#3F3F464D] !bg-[#18181B]"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateTo && "text-muted-foreground"
                )}
              >
                {localDateTo ? format(localDateTo, "PPP", { locale: ptBR }) : <span>Selecione uma data</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateTo}
                onSelect={setLocalDateTo}
                initialFocus
                disabled={(date) => localDateFrom ? date < localDateFrom : false}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="w-full flex gap-2 mt-auto">
          <Button
            onClick={handleClearFilters}
            variant="outline"
            className="flex-1 border-neutral-500 bg-transparent hover:bg-neutral-700 text-neutral-50"
          >
            <X className="w-4 h-4 mr-2" />
            Limpar
          </Button>
          <Button onClick={handleApplyFilters} className="flex-1">
            Filtrar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MoreFilterCRM;
